import { useState } from "react";
import { trpc, queryClient } from "../../lib/trpc";
import { useLanguage } from "../../contexts/LanguageContext";
import { fmtCurrency } from "../../lib/format";
import { Button } from "../../components/ui/button";
import { Select } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { THEAD_ROW } from "../admin/base-data-shared";
import { SettlementStatusBadge } from "../../components/settlement/SettlementStatusBadge";
import { monthKeyGmt8 } from "../../../../shared/settlement-period";

/**
 * 帳務結算工作台（dispatcher / admin）— Phase 7。
 * 月結手動產生草稿 → 開立 → 標記已付；金額全貌（gross/fee/net）。
 */
const NOW = new Date();
const THIS_YEAR = NOW.getFullYear();
const THIS_MONTH = NOW.getMonth() + 1;

export default function SettlementConsole() {
  const { t } = useLanguage();
  const [year, setYear] = useState(THIS_YEAR);
  const [month, setMonth] = useState(THIS_MONTH);
  const [partyType, setPartyType] = useState<"" | "agency" | "fleet">("");
  const [detailId, setDetailId] = useState<number | null>(null);
  const [confirm, setConfirm] = useState<{ id: number; kind: "issue" | "markPaid"; partyType: "agency" | "fleet" } | null>(null);
  const [genResult, setGenResult] = useState<{ created: number; updated: number; skipped: number; warnings: string[] } | null>(null);

  const listQ = trpc.settlement.listAll.useQuery({ year, month, partyType: partyType || undefined });
  const invalidate = () => queryClient.invalidateQueries();

  const genM = trpc.settlement.generateMonthly.useMutation({
    onSuccess: (r) => {
      setGenResult(r);
      invalidate();
    },
  });
  const issueM = trpc.settlement.issue.useMutation({
    onSuccess: () => {
      setConfirm(null);
      invalidate();
    },
  });
  const paidM = trpc.settlement.markPaid.useMutation({
    onSuccess: () => {
      setConfirm(null);
      invalidate();
    },
  });

  const detailQ = trpc.settlement.detail.useQuery(
    { id: detailId ?? 0 },
    { enabled: detailId != null },
  );

  const rows = listQ.data ?? [];
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <Select value={String(year)} onChange={(e) => setYear(Number(e.target.value))} className="w-28">
          {[THIS_YEAR - 1, THIS_YEAR, THIS_YEAR + 1].map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </Select>
        <Select value={String(month)} onChange={(e) => setMonth(Number(e.target.value))} className="w-24">
          {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
            <option key={m} value={m}>{String(m).padStart(2, "0")}</option>
          ))}
        </Select>
        <Select value={partyType} onChange={(e) => setPartyType(e.target.value as "" | "agency" | "fleet")} className="w-44">
          <option value="">{t({ "zh-TW": "全部端別", "en-US": "All parties", "zh-CN": "全部端别" })}</option>
          <option value="agency">{t({ "zh-TW": "需求方（平台應收）", "en-US": "Agencies (AR)", "zh-CN": "需求方（平台应收）" })}</option>
          <option value="fleet">{t({ "zh-TW": "供應方（平台應付）", "en-US": "Fleets (AP)", "zh-CN": "供应方（平台应付）" })}</option>
        </Select>
        <Button onClick={() => genM.mutate({ year, month })} disabled={genM.isPending}>
          {t({ "zh-TW": "產生本月草稿", "en-US": "Generate drafts", "zh-CN": "产生本月草稿" })}
        </Button>
      </div>

      {genM.error && <p className="text-[12px] text-destructive">{genM.error.message}</p>}
      {genResult && (
        <div className="rounded-lg border border-border bg-muted/30 p-3 text-[13px]">
          <p>
            {t({ "zh-TW": "產生結果：新建", "en-US": "Result: created", "zh-CN": "产生结果：新建" })} {genResult.created}、
            {t({ "zh-TW": "更新", "en-US": "updated", "zh-CN": "更新" })} {genResult.updated}、
            {t({ "zh-TW": "略過", "en-US": "skipped", "zh-CN": "略过" })} {genResult.skipped}
          </p>
          {genResult.warnings.map((w, i) => (
            <p key={i} className="text-amber-700 dark:text-amber-400">⚠ {w}</p>
          ))}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full text-[13px]">
          <thead>
            <tr className={THEAD_ROW}>
              <th className="p-3">{t({ "zh-TW": "對象", "en-US": "Party", "zh-CN": "对象" })}</th>
              <th className="p-3">{t({ "zh-TW": "期間", "en-US": "Period", "zh-CN": "期间" })}</th>
              <th className="p-3 text-right">{t({ "zh-TW": "單數", "en-US": "Orders", "zh-CN": "单数" })}</th>
              <th className="p-3 text-right">{t({ "zh-TW": "訂單總價", "en-US": "Gross", "zh-CN": "订单总价" })}</th>
              <th className="p-3 text-right">{t({ "zh-TW": "平台服務費", "en-US": "Fee", "zh-CN": "平台服务费" })}</th>
              <th className="p-3 text-right">{t({ "zh-TW": "淨額", "en-US": "Net", "zh-CN": "净额" })}</th>
              <th className="p-3">{t({ "zh-TW": "狀態", "en-US": "Status", "zh-CN": "状态" })}</th>
              <th className="p-3">{t({ "zh-TW": "操作", "en-US": "Actions", "zh-CN": "操作" })}</th>
            </tr>
          </thead>
          <tbody>
            {listQ.isLoading && (
              <tr><td className="p-4" colSpan={8}>…</td></tr>
            )}
            {!listQ.isLoading && rows.length === 0 && (
              <tr>
                <td className="p-4 text-muted-foreground" colSpan={8}>
                  {t({
                    "zh-TW": "本期間尚無結算單，先按「產生本月草稿」。",
                    "en-US": "No settlements for this period yet — generate drafts first.",
                    "zh-CN": "本期间尚无结算单，先按「产生本月草稿」。",
                  })}
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="border-t border-border align-middle">
                <td className="p-3">
                  <span className="font-medium">{r.partyName}</span>
                  <span className="ml-1 text-[11px] text-muted-foreground">
                    {r.partyType === "agency"
                      ? t({ "zh-TW": "需求方", "en-US": "Agency", "zh-CN": "需求方" })
                      : t({ "zh-TW": "供應方", "en-US": "Fleet", "zh-CN": "供应方" })}
                  </span>
                </td>
                <td className="p-3">
                  {monthKeyGmt8(r.periodStart)}{" "}
                  <span className="text-[11px] text-muted-foreground">S-{r.id}</span>
                </td>
                <td className="p-3 text-right">{r.totalOrders}</td>
                <td className="p-3 text-right">{fmtCurrency(r.grossAmount, r.currency)}</td>
                <td className="p-3 text-right">{fmtCurrency(r.platformFeeTotal, r.currency)}</td>
                <td className="p-3 text-right font-medium">{fmtCurrency(r.netAmount, r.currency)}</td>
                <td className="p-3"><SettlementStatusBadge status={r.status} /></td>
                <td className="space-x-1 whitespace-nowrap p-3">
                  <Button size="sm" variant="ghost" onClick={() => setDetailId(r.id)}>
                    {t({ "zh-TW": "明細", "en-US": "Items", "zh-CN": "明细" })}
                  </Button>
                  {r.status === "draft" && (
                    <>
                      <Button size="sm" onClick={() => setConfirm({ id: r.id, kind: "issue", partyType: r.partyType })}>
                        {t({ "zh-TW": "開立", "en-US": "Issue", "zh-CN": "开立" })}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={genM.isPending}
                        onClick={() => genM.mutate({ year, month, partyType: r.partyType, partyId: r.partyId })}
                      >
                        {t({ "zh-TW": "重產", "en-US": "Rebuild", "zh-CN": "重产" })}
                      </Button>
                    </>
                  )}
                  {r.status === "issued" && (
                    <Button size="sm" onClick={() => setConfirm({ id: r.id, kind: "markPaid", partyType: r.partyType })}>
                      {r.partyType === "agency"
                        ? t({ "zh-TW": "標記已收款", "en-US": "Mark received", "zh-CN": "标记已收款" })
                        : t({ "zh-TW": "標記已撥款", "en-US": "Mark paid out", "zh-CN": "标记已拨款" })}
                    </Button>
                  )}
                  {(r.status === "issued" || r.status === "paid") && (
                    <>
                      <a className="text-[12px] underline" href={`/api/settlements/${r.id}.xlsx`}>xlsx</a>{" "}
                      <a className="text-[12px] underline" href={`/api/settlements/${r.id}.pdf`}>PDF</a>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 確認對話框（開立 / 標記已付） */}
      <Dialog open={confirm != null} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        {/* 純確認、沒有輸入可丟，維持點外面即關閉 */}
        <DialogContent dismissible>
          <DialogHeader>
            <DialogTitle>
              {confirm?.kind === "issue"
                ? t({
                    "zh-TW": "確認開立結算單？開立後金額凍結，對方將收到通知。",
                    "en-US": "Issue this settlement? Amounts freeze and the party is notified.",
                    "zh-CN": "确认开立结算单？开立后金额冻结，对方将收到通知。",
                  })
                : confirm?.partyType === "agency"
                  ? t({
                      "zh-TW": "確認標記已收款？將連動釋放該需求方的信用額度。",
                      "en-US": "Mark as received? The agency's credit will be released.",
                      "zh-CN": "确认标记已收款？将连动释放该需求方的信用额度。",
                    })
                  : t({
                      "zh-TW": "確認標記已撥款給供應方？",
                      "en-US": "Mark as paid out to the fleet?",
                      "zh-CN": "确认标记已拨款给供应方？",
                    })}
            </DialogTitle>
          </DialogHeader>
          {(issueM.error || paidM.error) && (
            <p className="text-[12px] text-destructive">{issueM.error?.message ?? paidM.error?.message}</p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setConfirm(null)}>
              {t({ "zh-TW": "取消", "en-US": "Cancel", "zh-CN": "取消" })}
            </Button>
            <Button
              disabled={issueM.isPending || paidM.isPending}
              onClick={() =>
                confirm && (confirm.kind === "issue" ? issueM.mutate({ id: confirm.id }) : paidM.mutate({ id: confirm.id }))
              }
            >
              {t({ "zh-TW": "確認", "en-US": "Confirm", "zh-CN": "确认" })}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* 明細對話框 */}
      <Dialog open={detailId != null} onOpenChange={(o) => { if (!o) setDetailId(null); }}>
        {/* 唯讀明細，維持點外面即關閉 */}
        <DialogContent dismissible className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {t({ "zh-TW": "結算明細", "en-US": "Settlement items", "zh-CN": "结算明细" })} S-{detailId}
            </DialogTitle>
          </DialogHeader>
          {detailQ.isLoading ? (
            <p className="p-2 text-[13px]">…</p>
          ) : (
            <div className="max-h-[60vh] overflow-auto">
              <table className="w-full text-[13px]">
                <thead>
                  <tr className={THEAD_ROW}>
                    <th className="p-2">{t({ "zh-TW": "訂單編號", "en-US": "Order", "zh-CN": "订单编号" })}</th>
                    <th className="p-2">{t({ "zh-TW": "服務日期", "en-US": "Date", "zh-CN": "服务日期" })}</th>
                    <th className="p-2 text-right">{t({ "zh-TW": "金額", "en-US": "Amount", "zh-CN": "金额" })}</th>
                  </tr>
                </thead>
                <tbody>
                  {(detailQ.data?.items ?? []).map((it) => (
                    <tr key={it.orderId} className="border-t border-border">
                      <td className="p-2 font-mono text-[12px]">{it.orderNumber}</td>
                      <td className="p-2">
                        {new Date(it.pickupTime).toLocaleDateString("zh-TW", { timeZone: "Asia/Taipei" })}
                      </td>
                      <td className="p-2 text-right">
                        {fmtCurrency(it.amount, detailQ.data?.settlement.currency ?? "HKD")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
